import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  ForEach,
  Menu,
  If,
  P,
  Clickable,
  Icon,
} from '@fashionnovateam/nova-ui-toolkit';
import cn from 'classnames';
import { useLocation } from 'react-router-dom';
import {
  PoFilters,
  PaginatedSearchParams,
  useGetPurchaseOrdersSubStageCount,
} from '../../Lib/Hooks/purchase-orders.hooks';
import {
  parsePaginatedSearchParams,
  aggregateFilters,
  getFiltersItemsFromAggregatedFilters,
} from './utils';
import { startingFilters } from './constant';

import { StatusFilterItem, StatusFilterGroup } from './types';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';

interface PurchaseOrderFiltersProps {
  onFilter: (value: PoFilters) => void;
}

const PurchaseOrderFilters: React.FC<PurchaseOrderFiltersProps> =
  function PurchaseOrderFilters({ onFilter }) {
    const { flags } = React.useContext(FeatureFlagsContext);
    const { search, pathname } = useLocation();
    const params: PaginatedSearchParams = useMemo(
      () =>
        parsePaginatedSearchParams(
          search,
          flags.cancelled_po?.enabled,
          flags.po_revision?.enabled,
        ),
      [search, flags.cancelled_po?.enabled, flags.po_revision?.enabled],
    );
    const [currentFilters, setCurrentFilters] = useState<
      StatusFilterItem[] | null | undefined
    >(
      getFiltersItemsFromAggregatedFilters(
        params.filter,
        flags.cancelled_po && flags.cancelled_po.enabled,
        flags.po_revision && flags.po_revision.enabled,
      ),
    );
    const userId = useMemo(() => pathname.split('/')[2], [pathname]);
    const { data: poCountBySubStage, isLoading } =
      useGetPurchaseOrdersSubStageCount(userId, params);

    const filteredStartingFilters = useMemo(() => {
      let filteredFilters: StatusFilterGroup[] = [...startingFilters];
      if (
        !flags.cancelled_po ||
        (flags.cancelled_po && !flags.cancelled_po.enabled)
      ) {
        filteredFilters = filteredFilters.filter(
          (filter) => !filter.items.find((item) => item.key === 'cancelled'),
        );
      }
      if (
        !flags.po_revision ||
        (flags.po_revision && !flags.po_revision.enabled)
      ) {
        filteredFilters = filteredFilters.filter(
          (filter) =>
            !filter.items.find((item) => item.key === 'revision-requested'),
        );
      }
      return filteredFilters;
    }, [flags]);

    const [filters, setFilters] = React.useState<StatusFilterGroup[]>(
      filteredStartingFilters,
    );

    useEffect(() => {
      const updateBadges = async () => {
        const updatedFilters = filters.map((filter) => {
          const updatedItems = filter.items.map((item) => {
            if (item.key === 'cancelled') {
              return {
                ...item,
                badge:
                  poCountBySubStage?.find(
                    ({ sub_stage }) => sub_stage === 'Cancelled',
                  )?.count || 0,
              };
            }
            if (item.key === 'revision-requested') {
              return {
                ...item,
                badge:
                  poCountBySubStage?.find(
                    ({ sub_stage }) => sub_stage === 'Revision Requested',
                  )?.count || 0,
              };
            }
            const matchedCount = poCountBySubStage?.find(
              ({ sub_stage }) => sub_stage === item.filters.sub_stages?.[0],
            );
            return {
              ...item,
              badge: matchedCount?.count || 0,
            };
          });

          return {
            ...filter,
            items: updatedItems,
          };
        });
        setFilters(updatedFilters);
      };

      updateBadges().catch((err) => console.log(err));
    }, [poCountBySubStage]);

    useEffect(() => {
      if (currentFilters) {
        onFilter(aggregateFilters(currentFilters));
      }
    }, [currentFilters]);

    const handleFilterClick = (filterItem: StatusFilterItem) => {
      const isFilterAlreadyApplied = currentFilters?.some(
        (filter) => filter.key === filterItem.key,
      );
      if (isFilterAlreadyApplied) {
        setCurrentFilters((prev) => {
          const newFilters = prev?.filter(
            (filter) => filter.key !== filterItem.key,
          );
          return newFilters;
        });
      } else {
        setCurrentFilters((prev) => {
          if (prev) {
            return [...prev, filterItem];
          }
          return [filterItem];
        });
      }
    };

    const handleFilterGroupClick = (filterGroup: StatusFilterGroup) => {
      // Apply all filters not already applied in the group
      const newFilters = filterGroup.items.filter(
        (item) => !currentFilters?.some((filter) => filter.key === item.key),
      );

      // Check if all filters from this group are already applied
      const allGroupFiltersApplied = newFilters.length === 0;

      if (allGroupFiltersApplied) {
        // Remove only this group's filters, keep filters from other groups
        setCurrentFilters(
          currentFilters?.filter(
            (filter) =>
              !filterGroup.items.some((item) => item.key === filter.key),
          ) || null,
        );
      } else {
        // Add new filters while keeping existing ones
        setCurrentFilters([...(currentFilters || []), ...newFilters]);
      }
    };

    const getStageCount = (group: StatusFilterGroup) =>
      group.items.reduce(
        (acc: number, item) => (item.badge ? item.badge + acc : acc),
        0,
      );

    return (
      <div className="lg:flex flex-row border-r nu-border-neutral-200">
        <Stack direction="col" className="overflow-auto h-full py-5 xl:py-6">
          <ForEach items={filters}>
            {(group, index) => (
              <React.Fragment key={`${index}-${group.title}`}>
                <If condition={index}>
                  <div className="py-4 px-5 w-full">
                    <div className="self-stretch border-b border-neutral-200" />
                  </div>
                </If>
                <Menu.Group>
                  <Stack direction="col" gap="sm">
                    <If condition={group.title}>
                      <Clickable onClick={() => handleFilterGroupClick(group)}>
                        <div
                          className={`text-left pl-8 py-[2px] text-2xs font-normal hover:text-base-primary ${
                            group.items.every((item) =>
                              currentFilters?.some(
                                (filter) => filter.key === item.key,
                              ),
                            )
                              ? 'text-primary-700'
                              : 'text-base-disabled'
                          }`}
                        >
                          {`${group.title} (${getStageCount(group).toLocaleString('en-US')})`}
                        </div>
                      </Clickable>
                    </If>
                    <ForEach items={group.items}>
                      {(item) => (
                        <Clickable onClick={() => handleFilterClick(item)}>
                          <Menu.Item
                            key={item.filters.sub_stages?.[0]}
                            className="flex text-left pl-2 bg-neutral-0"
                          >
                            <Icon.CheckTickSingle
                              className={`text-primary-700 text-lg ${
                                currentFilters &&
                                currentFilters.some(
                                  (filter) => filter.key === item.key,
                                )
                                  ? ''
                                  : 'invisible'
                              }`}
                            />
                            <Stack direction="row" className="justify-between">
                              <P
                                size="xsm"
                                color="secondary"
                                className="text-nowrap"
                              >
                                {item.label}
                              </P>
                              <If condition={item.badge !== undefined}>
                                <P
                                  color="disabled"
                                  size="2xs"
                                  className={cn(
                                    'h-4 px-1 bg-neutral-200 rounded',
                                    {
                                      'animate-pulse': isLoading,
                                    },
                                  )}
                                >
                                  {item?.badge?.toLocaleString('en-US')}
                                </P>
                              </If>
                            </Stack>
                          </Menu.Item>
                        </Clickable>
                      )}
                    </ForEach>
                  </Stack>
                </Menu.Group>
              </React.Fragment>
            )}
          </ForEach>
        </Stack>
      </div>
    );
  };

export default PurchaseOrderFilters;
